// action-plan.ts — Logique pure des plans d'action (liste + phases en DAG + KPI).
//
// Extrait de modules/page-action-plans.ts pour être testable hors DOM (vitest),
// dans la lignée de structure-graph.ts (dont ce module est le pendant « plan »).
// Le monolithe garde la glu DOM/API et délègue ici toute DÉCISION : tri de la
// liste, validité d'un drag&drop de prédécesseur, filtre des actions terminées,
// règle de validation d'une phase intelligente, ré-évaluation de la date de fin
// KPI, détection de relink sans effet, pourcentage protégé.
//
// Toutes les comparaisons de dates portent sur des chaînes ISO 'YYYY-MM-DD' :
// l'ordre lexical == l'ordre chronologique, pas de Date() (donc pas de fuseau).

// ── 1. Tri de la liste des plans ──────────────────────────────────────────────

export type PlanSortKey = 'code' | 'title' | 'date_start' | 'date_end' | 'avancement' | 'meteo';

/** Forme minimale d'un plan pour le tri (sous-ensemble de PlanRow). */
export interface SortablePlan {
  code: string;
  title: string;
  date_start: string | null;
  date_end: string | null;
  avancement: number | null;
  meteo: string | null;
}

/**
 * Trie une liste de plans par `key` / direction, sans muter l'entrée.
 *
 * - `avancement` : tri numérique, `null` traité comme -1 (donc en queue en asc).
 * - colonnes texte (`code`, `title`, `meteo`) : comparaison insensible à la
 *   casse (lowercase), `null` → chaîne vide.
 * - dates : comparaison lexicale ISO, `null` → chaîne vide (donc en tête en asc).
 */
export function sortPlans<T extends SortablePlan>(
  rows: ReadonlyArray<T>,
  key: PlanSortKey,
  asc: boolean,
): T[] {
  const dir = asc ? 1 : -1;
  const val = (p: T): string | number => {
    switch (key) {
      case 'avancement':
        return p.avancement ?? -1;
      case 'meteo':
        return (p.meteo || '').toLowerCase();
      case 'date_start':
        return p.date_start || '';
      case 'date_end':
        return p.date_end || '';
      case 'title':
        return (p.title || '').toLowerCase();
      default:
        return (p.code || '').toLowerCase();
    }
  };
  return [...rows].sort((a, b) => {
    const va = val(a);
    const vb = val(b);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });
}

// ── 2. Drag & drop de prédécesseur (validité de la cible) ─────────────────────

export interface DragState {
  /** Phase déplacée. */
  child: number;
  /** Prédécesseur sous lequel elle était affichée (null = racine). */
  fromParent: number | null;
}

/** `valid` = dépôt accepté ; `noop` = cible == prédécesseur d'origine (aucun
 *  changement) ; `invalid` = cible == phase elle-même ou un de ses descendants
 *  (créerait un cycle), ou pas de drag en cours. */
export type DropStatus = 'valid' | 'invalid' | 'noop';

/**
 * Statut d'une cible de dépôt pendant un drag de prédécesseur.
 *
 * `descendantsOfChild` = IDs atteignables depuis la phase déplacée (cf.
 * structure-graph.descendantIds) : déposer sur l'un d'eux fermerait un cycle.
 */
export function dropTargetStatus(
  drag: DragState | null,
  targetId: number,
  descendantsOfChild: ReadonlySet<number>,
): DropStatus {
  if (!drag) return 'invalid';
  if (targetId === drag.child) return 'invalid';
  if (descendantsOfChild.has(targetId)) return 'invalid';
  if (targetId === drag.fromParent) return 'noop';
  return 'valid';
}

/**
 * Un relink change-t-il réellement le jeu de prédécesseurs ? Vrai (== « ne
 * change rien ») si même cardinalité ET mêmes éléments (ordre ignoré) → le
 * caller saute le PUT. Hypothèse : pas de doublon dans `before`/`after` (jeux
 * de prédécesseurs).
 */
export function relinkChangesNothing(
  before: ReadonlyArray<number>,
  after: ReadonlyArray<number>,
): boolean {
  return before.length === after.length && after.every((p) => before.includes(p));
}

// ── 3. Actions d'une phase : filtre « terminées » ─────────────────────────────

/** Forme minimale d'une action pour le filtre (sous-ensemble de RowAction). */
export interface CompletableAction {
  status_id?: number | null;
}

/**
 * Filtre les actions terminées d'une phase. Si `showCompleted`, renvoie tout ;
 * sinon retire les actions dont le statut est final. Une action sans statut
 * (`status_id` null/absent) est TOUJOURS conservée (jamais « terminée »).
 */
export function filterCompletedActions<T extends CompletableAction>(
  actions: ReadonlyArray<T>,
  finalStatusIds: ReadonlySet<number>,
  showCompleted: boolean,
): T[] {
  if (showCompleted) return [...actions];
  return actions.filter((a) => a.status_id == null || !finalStatusIds.has(a.status_id));
}

/**
 * Alerte d'échéance d'une phase : la date de fin de l'action la plus tardive
 * (révisée si présente, sinon butoir) dépasse-t-elle la date de fin cible ?
 * Faux si la phase n'a pas de date cible, ou si aucune action n'a de date.
 */
export interface DeadlinePhase {
  date_end_target: string | null;
  actions?: ReadonlyArray<{ deadline?: string | null; deadline_revised?: string | null }>;
}

export function phaseDeadlineAlert(phase: DeadlinePhase): boolean {
  if (!phase.date_end_target) return false;
  let maxEnd = '';
  for (const a of phase.actions || []) {
    const d = a.deadline_revised || a.deadline || '';
    if (d && d > maxEnd) maxEnd = d;
  }
  return maxEnd !== '' && maxEnd > phase.date_end_target;
}

// ── 4. Phase intelligente : règle de validation des critères ──────────────────

/** Critères d'une phase intelligente. Le comité ne suffit PAS à lui seul. */
export interface SmartCriteria {
  programId?: number | null;
  projectId?: number | null;
  structureId?: number | null;
  committeeId?: number | null;
}

/**
 * Une phase intelligente est valide si AU MOINS un critère parmi
 * programme / projet / organisation est renseigné. Règle métier subtile : le
 * comité seul ne qualifie PAS la phase (cf. page-action-plans : le comité est
 * exclu du contrôle « au moins un »).
 */
export function smartCriteriaValid(c: SmartCriteria): boolean {
  return !!(c.programId || c.projectId || c.structureId);
}

// ── 5. KPI : ré-évaluation de la date de fin + pourcentage protégé ────────────

/**
 * Date de fin RÉÉVALUÉE = la plus tardive (ISO) parmi la cible courante et les
 * cibles historisées. `null` si aucune n'est renseignée.
 */
export function reevaluatedEnd(
  currentTarget: string | null,
  history: ReadonlyArray<{ phase_date_end_target: string | null }>,
): string | null {
  let end = currentTarget;
  for (const h of history) {
    if (h.phase_date_end_target && (!end || h.phase_date_end_target > end)) {
      end = h.phase_date_end_target;
    }
  }
  return end;
}

/**
 * Pourcentage entier protégé : `round(100 * numerator / denominator)`, ou 0 si
 * le dénominateur est nul ou négatif (aucune action → 0 %, jamais NaN/∞).
 * Factorise le calcul dupliqué des graphes « % bloquées » et « % mises à jour ».
 */
export function pct(numerator: number, denominator: number): number {
  return denominator > 0 ? Math.round((100 * numerator) / denominator) : 0;
}
